/**
 * Mapbox API client helpers
 *
 * Référence : docs/architecture/mapbox-fmd-v1a.md
 *
 * Wrapper léger autour des APIs Mapbox (Directions, Tilequery, Geocoding,
 * Static Images, Search Box). Réutilisable depuis tous les composants
 * frontend qui ont besoin de Mapbox.
 *
 * IMPORTANT : ce fichier ne doit PAS importer mapbox-gl (SDK ~250 KB).
 * Il fait uniquement des fetch HTTP. Pour le rendu de carte interactive,
 * utiliser MAPBOX_CONFIG + initMapbox depuis @/config/mapbox.ts.
 *
 * Token : utilise VITE_MAPBOX_PUBLIC_TOKEN (idem que mapbox-gl). Pour les
 * appels server-side (edge functions), utiliser MAPBOX_TOKEN secret côté
 * Deno.
 */

const MAPBOX_BASE = 'https://api.mapbox.com';

// Audit Lovable 2026-05-09 : retrait du FALLBACK_TOKEN hardcodé (cf. src/config/mapbox.ts).
// En prod le token DOIT être set via VITE_MAPBOX_PUBLIC_TOKEN. En dev, warn si manquant.
const ENV_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN;

if (!ENV_TOKEN) {
  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_MAPBOX_PUBLIC_TOKEN missing: required in production build. ' +
      'Configure in deployment env vars.',
    );
  } else {
     
    console.warn(
      '[Mapbox client] VITE_MAPBOX_PUBLIC_TOKEN missing in dev environment. ' +
      'Set it in .env to enable Mapbox API helpers.',
    );
  }
}

const TOKEN = ENV_TOKEN ?? '';

// =====================================================
// Types
// =====================================================

export interface LngLat {
  lng: number;
  lat: number;
}

export type DirectionsProfile = 'driving' | 'walking' | 'cycling' | 'driving-traffic';

export interface DirectionsResult {
  distanceM: number;
  durationSec: number;
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
}

export interface TilequeryFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: {
    class?: string;
    type?: string;
    name?: string;
    name_fr?: string;
    distance?: number;
    [key: string]: unknown;
  };
}

export interface ReverseGeocodingResult {
  label: string;
  street?: string;
  postalCode?: string;
  city?: string;
}

export interface SearchBoxSuggestion {
  mapboxId: string;
  name: string;
  fullAddress?: string;
  placeFormatted?: string;
  /**
   * Type du résultat tel que Mapbox le renvoie (`address`, `street`,
   * `postcode`, `place`, `locality`, `neighborhood`). Sert à choisir le bon
   * libellé de zone : pour une ville, `placeFormatted` est son département.
   */
  featureType?: string;
}

export interface StaticMapMarker {
  lat: number;
  lng: number;
  color?: string; // hex sans #
  label?: string;
}

// =====================================================
// Directions API
// =====================================================

export interface DirectionsOptions {
  /**
   * Heure de départ ISO 8601 (ex : "2026-07-01T08:00") pour un ETA tenant
   * compte du trafic prévu. Uniquement pris en compte avec le profil
   * `driving-traffic` (contrainte Mapbox). Ignoré sinon.
   */
  departAt?: string;
}

/**
 * Mapbox Directions API.
 * Doc : https://docs.mapbox.com/api/navigation/directions/
 *
 * @param from Coordonnées départ
 * @param to Coordonnées arrivée
 * @param profile driving (default) | walking | cycling | driving-traffic
 * @param opts   Options (depart_at pour ETA trafic sur driving-traffic)
 */
export async function fetchDirections(
  from: LngLat,
  to: LngLat,
  profile: DirectionsProfile = 'driving',
  opts: DirectionsOptions = {}
): Promise<DirectionsResult | null> {
  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    access_token: TOKEN,
  });
  // depart_at n'est supporté que sur driving-traffic (doc Mapbox Directions).
  if (profile === 'driving-traffic' && opts.departAt) {
    params.set('depart_at', opts.departAt);
  }
  const url = `${MAPBOX_BASE}/directions/v5/mapbox/${profile}/${from.lng},${from.lat};${to.lng},${to.lat}?${params.toString()}`;

  const res = await fetch(url);
  if (!res.ok) {
    return null;
  }
  const data = await res.json();
  if (!data.routes || data.routes.length === 0) return null;

  const route = data.routes[0];
  return {
    distanceM: route.distance,
    durationSec: route.duration,
    geometry: route.geometry,
  };
}

/**
 * Mapbox Directions API avec un point de passage (3 points : from → via → to).
 * Sert à mesurer le VRAI détour conducteur côté client (route via passager
 * moins route directe), avec la même logique que le serveur.
 * Doc : https://docs.mapbox.com/api/navigation/directions/
 */
export async function fetchDirectionsVia(
  from: LngLat,
  via: LngLat,
  to: LngLat,
  profile: DirectionsProfile = 'driving',
  opts: DirectionsOptions = {}
): Promise<DirectionsResult | null> {
  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    access_token: TOKEN,
  });
  if (profile === 'driving-traffic' && opts.departAt) {
    params.set('depart_at', opts.departAt);
  }
  const coords =
    `${from.lng},${from.lat};${via.lng},${via.lat};${to.lng},${to.lat}`;
  const url = `${MAPBOX_BASE}/directions/v5/mapbox/${profile}/${coords}?${params.toString()}`;

  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.routes || data.routes.length === 0) return null;

  const route = data.routes[0];
  return {
    distanceM: route.distance,
    durationSec: route.duration,
    geometry: route.geometry,
  };
}

/**
 * Mapbox Directions API avec N points de passage ordonnés (covoiturage
 * multi-passagers : origine → pickup1 → pickup2 → ... → bureau).
 * L'API accepte jusqu'à 25 coordonnées en UN seul appel (profil driving),
 * l'ordre des points est respecté tel quel (pas de ré-optimisation ici :
 * l'optimisation TSP est un second temps via l'Optimization API).
 * Doc : https://docs.mapbox.com/api/navigation/directions/
 *
 * @param points Liste ordonnée de 2 à 25 coordonnées (>=2 requis).
 */
export async function fetchDirectionsWaypoints(
  points: LngLat[],
  profile: DirectionsProfile = 'driving',
  opts: DirectionsOptions = {}
): Promise<DirectionsResult | null> {
  if (points.length < 2) return null;
  const capped = points.slice(0, 25); // limite dure Mapbox Directions
  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    access_token: TOKEN,
  });
  if (profile === 'driving-traffic' && opts.departAt) {
    params.set('depart_at', opts.departAt);
  }
  const coords = capped.map((p) => `${p.lng},${p.lat}`).join(';');
  const url = `${MAPBOX_BASE}/directions/v5/mapbox/${profile}/${coords}?${params.toString()}`;

  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.routes || data.routes.length === 0) return null;

  const route = data.routes[0];
  return {
    distanceM: route.distance,
    durationSec: route.duration,
    geometry: route.geometry,
  };
}

// =====================================================
// Tilequery API (POI search)
// =====================================================

/**
 * Mapbox Tilequery API : trouve des POI dans un rayon.
 * Doc : https://docs.mapbox.com/api/maps/tilequery/
 *
 * @param center Centre de recherche
 * @param radiusM Rayon en mètres (max 1000)
 * @param layers Layers à interroger (default : poi_label,transit_stop_label)
 */
export async function fetchTilequery(
  center: LngLat,
  radiusM: number = 300,
  layers: string[] = ['poi_label', 'transit_stop_label']
): Promise<TilequeryFeature[]> {
  const layersParam = layers.join(',');
  const url = `${MAPBOX_BASE}/v4/mapbox.mapbox-streets-v8/tilequery/${center.lng},${center.lat}.json?radius=${radiusM}&layers=${layersParam}&limit=20&access_token=${TOKEN}`;

  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.features ?? []) as TilequeryFeature[];
}

// =====================================================
// Geocoding API (reverse)
// =====================================================

/**
 * Mapbox Geocoding API v6 : reverse geocoding (lat/lng → label).
 * Doc : https://docs.mapbox.com/api/search/geocoding/
 */
export async function fetchReverseGeocoding(
  point: LngLat,
  language: string = 'fr'
): Promise<ReverseGeocodingResult | null> {
  const url = `${MAPBOX_BASE}/search/geocode/v6/reverse?longitude=${point.lng}&latitude=${point.lat}&language=${language}&types=address,street,locality&access_token=${TOKEN}`;

  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.features || data.features.length === 0) return null;

  const feature = data.features[0];
  const props = feature.properties ?? {};
  const ctx = props.context ?? {};

  return {
    label: props.full_address ?? props.name ?? 'Lieu inconnu',
    street: ctx.street?.name,
    postalCode: ctx.postcode?.name,
    city: ctx.place?.name,
  };
}

// =====================================================
// Search Box API (autocomplete)
// =====================================================

/**
 * Mapbox Search Box API : autocomplete suggestions.
 * Doc : https://docs.mapbox.com/api/search/search-box/
 *
 * @param query Texte saisi par l'utilisateur
 * @param sessionToken UUID v4 unique par session de recherche (économie billing)
 * @param country Code pays (default : fr)
 */
export async function fetchSearchBoxSuggest(
  query: string,
  sessionToken: string,
  country: string = 'fr'
): Promise<SearchBoxSuggestion[]> {
  if (query.length < 3) return [];

  // `types` est OBLIGATOIRE ici (22/08). Sans filtre, Search Box propose aussi
  // les departements et les regions : un collegue a enregistre
  // « Pyrenees-Orientales, France » comme zone covoit, soit un territoire de
  // plus de 100 km de large, inutilisable pour un point de rendez-vous alors
  // que le matching travaille a 15 km. On ne propose donc que des
  // granularites ou l'on peut reellement se retrouver, de l'adresse precise
  // au quartier. `address` est inclus : celui qui VEUT saisir son adresse en
  // toutes lettres le peut, la precision reste son choix.
  const url = `${MAPBOX_BASE}/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&session_token=${sessionToken}&country=${country}&language=fr&limit=5&types=address,street,postcode,place,locality,neighborhood&access_token=${TOKEN}`;

  const res = await fetch(url);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.suggestions ?? []).map((s: any) => ({
    mapboxId: s.mapbox_id,
    name: s.name,
    fullAddress: s.full_address,
    placeFormatted: s.place_formatted,
    featureType: s.feature_type,
  })) as SearchBoxSuggestion[];
}

/**
 * Search Box retrieve : récupère les coordonnées d'une suggestion.
 * Doc : https://docs.mapbox.com/api/search/search-box/#retrieve
 */
export async function fetchSearchBoxRetrieve(
  mapboxId: string,
  sessionToken: string
): Promise<{ lng: number; lat: number; label: string } | null> {
  const url = `${MAPBOX_BASE}/search/searchbox/v1/retrieve/${mapboxId}?session_token=${sessionToken}&access_token=${TOKEN}`;

  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  if (!data.features || data.features.length === 0) return null;

  const feature = data.features[0];
  const [lng, lat] = feature.geometry.coordinates;
  return {
    lng,
    lat,
    label: feature.properties.full_address ?? feature.properties.name ?? '',
  };
}

// =====================================================
// Static Images API (URL builder)
// =====================================================

export interface StaticMapOptions {
  center: LngLat;
  zoom?: number;
  width?: number;
  height?: number;
  markers?: StaticMapMarker[];
  retina?: boolean;
  style?: string;
}

/**
 * Mapbox Static Images API URL builder.
 * Doc : https://docs.mapbox.com/api/maps/static-images/
 *
 * Utilisé pour : push notifs riches, miniatures TripCard, PDF attestations.
 */
export function buildStaticMapUrl(options: StaticMapOptions): string {
  const {
    center,
    zoom = 14,
    width = 300,
    height = 200,
    markers = [],
    retina = true,
    style = 'streets-v12',
  } = options;

  const markersStr = markers
    .map(m => {
      const color = m.color ?? 'ff5722';
      const label = m.label ?? '';
      return `pin-s${label ? '-' + label : ''}+${color}(${m.lng},${m.lat})`;
    })
    .join(',');

  const overlay = markersStr ? `${markersStr}/` : '';
  const retinaSuffix = retina ? '@2x' : '';

  return `${MAPBOX_BASE}/styles/v1/mapbox/${style}/static/${overlay}${center.lng},${center.lat},${zoom}/${width}x${height}${retinaSuffix}?access_token=${TOKEN}`;
}

// =====================================================
// Helpers
// =====================================================

/**
 * Génère un session_token UUID v4 pour Search Box billing.
 * À conserver dans un state local pendant toute une session de recherche
 * (suggest + retrieve = 1 seule facturation).
 */
export function generateSearchSessionToken(): string {
  return crypto.randomUUID();
}

/**
 * Distance approximative entre 2 points en mètres (formule haversine).
 * Pour calculs côté client uniquement (sans turf.js).
 */
export function haversineDistanceM(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;

  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
}
